import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { CircularProgress, Popper, makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import useGlobalQuickSearch from '../../hooks/useGlobalQuickSearch';
import { GLOBAL_SEARCH_LISTBOX_ID, GLOBAL_SEARCH_MIN_QUERY_LENGTH } from '../../helper/constants';
import BrandingColors from '../../styles/color';
import StudentSearchResult from './StudentSearchResult';

const useStyles = makeStyles(() => ({
    popperRoot: {
        zIndex: 1400
    },
    overlay: {
        width: '560px',
        maxWidth: 'calc(100vw - 24px)',
        maxHeight: 'min(70vh, 480px)',
        overflowY: 'auto',
        backgroundColor: BrandingColors.white,
        border: `1px solid ${BrandingColors.lightGrey}`,
        borderRadius: '3px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
        fontFamily: 'FiraSans-Regular',
        '@media (max-width: 767px)': {
            width: 'calc(100vw - 24px)'
        },
        '@media (prefers-reduced-motion: reduce)': {
            transition: 'none'
        }
    },
    listReset: {
        listStyle: 'none',
        margin: 0,
        padding: 0
    },
    status: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px',
        fontSize: '12px',
        color: BrandingColors.darkGreyFont
    },
    empty: {
        padding: '12px',
        fontSize: '12px',
        color: BrandingColors.darkGreyFont
    },
    emptyHint: {
        display: 'block',
        marginTop: '4px',
        color: BrandingColors.lightGreyFont
    },
    errorRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px',
        fontSize: '12px',
        color: BrandingColors.errorColor
    },
    retry: {
        fontFamily: 'FiraSans-Regular',
        fontSize: '12px',
        color: BrandingColors.linkTextFont,
        background: 'transparent',
        border: 'none',
        padding: '4px 8px',
        borderRadius: '2px',
        cursor: 'pointer',
        textDecoration: 'underline',
        '&:focus-visible': {
            outline: `2px solid ${BrandingColors.textBord}`,
            outlineOffset: '1px'
        }
    },
    topBar: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: '6px 12px',
        borderBottom: `1px solid ${BrandingColors.customGrey}`
    },
    bottomBar: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: '6px 12px',
        borderTop: `1px solid ${BrandingColors.customGrey}`
    },
    showMore: {
        fontFamily: 'FiraSans-Regular',
        fontSize: '12px',
        color: BrandingColors.linkTextFont,
        background: 'transparent',
        border: 'none',
        padding: '2px 4px',
        cursor: 'pointer',
        textDecoration: 'underline',
        '&:hover': {
            backgroundColor: BrandingColors.hoverBlue
        },
        '&:focus-visible': {
            outline: `2px solid ${BrandingColors.textBord}`,
            outlineOffset: '1px'
        }
    },
    live: {
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0,0,0,0)',
        whiteSpace: 'nowrap',
        border: 0
    }
}));

function GlobalSearchResults({ query, moduleFilter, maxResults, onClose, onShowMore, listboxId, anchorEl }) {
    const classes = useStyles();
    const { t } = useTranslation();
    const { loading, error, results, hasMore, refetch } = useGlobalQuickSearch(query, maxResults);
    const containerRef = useRef(null);

    useEffect(() => {
        const onClickOutside = (event) => {
            const target = event.target;
            const insideOverlay = containerRef.current && containerRef.current.contains(target);
            const insideAnchor = anchorEl && anchorEl.contains(target);
            if (!insideOverlay && !insideAnchor) {
                onClose?.();
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [onClose, anchorEl]);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                onClose?.();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    const trimmed = (query ?? '').trim();
    if (trimmed.length < GLOBAL_SEARCH_MIN_QUERY_LENGTH) {
        return null;
    }

    const buildAnnouncement = () => {
        if (error) return t('sidebar.search.errorLoading');
        if (loading) return '';
        if (results.length === 0) return t('sidebar.search.noResults', { query: trimmed });
        return t('sidebar.search.resultsAnnouncement', { count: results.length });
    };

    const announcement = buildAnnouncement();

    const renderSeeAllButton = () => (
        <button
            type="button"
            className={classes.showMore}
            onClick={() => {
                onShowMore?.(trimmed, moduleFilter);
                onClose?.();
            }}
        >
            {t('sidebar.search.showMoreResults', { query: trimmed })}
        </button>
    );

    return (
        <Popper
            open
            anchorEl={anchorEl}
            placement="bottom-start"
            className={classes.popperRoot}
            modifiers={{
                flip: { enabled: false },
                preventOverflow: { enabled: true, boundariesElement: 'viewport' }
            }}
        >
        <div
            ref={containerRef}
            id={listboxId}
            role="listbox"
            aria-label={t('sidebar.search.resultsLabel')}
            className={classes.overlay}
        >
            <div className={classes.live} aria-live="polite" aria-atomic="true">
                {announcement}
            </div>

            {!loading && !error && hasMore && (
                <div className={classes.topBar}>
                    {renderSeeAllButton()}
                </div>
            )}

            {loading && (
                <div className={classes.status}>
                    <CircularProgress size={16} />
                    <span>{t('sidebar.search.loading')}</span>
                </div>
            )}

            {!loading && error && (
                <div className={classes.errorRow}>
                    <span>{t('sidebar.search.errorLoading')}</span>
                    <button
                        type="button"
                        className={classes.retry}
                        aria-label={t('sidebar.search.retry')}
                        onClick={refetch}
                    >
                        {t('sidebar.search.retry')}
                    </button>
                </div>
            )}

            {!loading && !error && results.length === 0 && (
                <div className={classes.empty}>
                    <span>{t('sidebar.search.noResults', { query: trimmed })}</span>
                    <span className={classes.emptyHint}>{t('sidebar.search.noResultsHint')}</span>
                </div>
            )}

            {!loading && !error && results.length > 0 && (
                <ul className={classes.listReset}>
                    {results.map((person) => (
                        <StudentSearchResult
                            key={`${person.entityType || 'student'}-${person.entityId ?? person.studentUID}`}
                            student={person}
                            entityType={person.entityType}
                            moduleFilter={moduleFilter}
                            onNavigate={onClose}
                        />
                    ))}
                </ul>
            )}

            {!loading && !error && hasMore && results.length > 0 && (
                <div className={classes.bottomBar}>
                    {renderSeeAllButton()}
                </div>
            )}

        </div>
        </Popper>
    );
}

GlobalSearchResults.propTypes = {
    query: PropTypes.string,
    moduleFilter: PropTypes.string,
    maxResults: PropTypes.number,
    onClose: PropTypes.func,
    onShowMore: PropTypes.func,
    listboxId: PropTypes.string,
    anchorEl: PropTypes.any
};

GlobalSearchResults.defaultProps = {
    query: '',
    moduleFilter: 'All',
    maxResults: 10,
    onClose: undefined,
    onShowMore: undefined,
    listboxId: GLOBAL_SEARCH_LISTBOX_ID,
    anchorEl: null
};

export default GlobalSearchResults;
